import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { SiteNoteAssignee } from 'src/app/models/site-notes/site-note-assignee.model';
import { SiteNoteRequest, UpdateSiteNoteDto } from 'src/app/models/site-notes/site-note-request.model';
import { SiteNoteResource, SiteNoteResourceType } from 'src/app/models/site-notes/site-note-resource-type.model';
import { SiteNote } from 'src/app/models/site-notes/site-note.model';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { SiteNotesService } from 'src/app/services/sections/site-notes/site-notes.service';
import { ToastService } from 'src/app/services/ui/toast.service';
import { ToastType } from '../../toast/models/toast-type.enum';

@Component({
	selector: 'site-note-add-template',
	templateUrl: './site-note-add-template.component.html',
	styleUrls: ['./site-note-add-template.component.scss', '../../stylesheets/job-actions.scss', '../../stylesheets/job-main.scss', '../../stylesheets/job-inputs.scss']
})
export class SiteNoteAddTemplateComponent implements OnInit {
	@Input() public siteNote: SiteNote = null;
	@Input() public jobId: number = null;
	@Input() public isOnCompleteNote: boolean;
	@Input() public possibleAssignees: SiteNoteAssignee[] = [];
	@Input() public accountSiteId: number = null;

	@Input() public assetTypes: SiteNoteResourceType[] = [];
	@Input() public locationTypes: SiteNoteResourceType[] = [];

	// tslint:disable-next-line:no-output-on-prefix
	@Output() public onSiteNoteAddSave: EventEmitter<SiteNoteRequest> = new EventEmitter();
	// tslint:disable-next-line:no-output-on-prefix
	@Output() public onSiteNoteEditSave: EventEmitter<UpdateSiteNoteDto> = new EventEmitter();
	// tslint:disable-next-line:no-output-on-prefix
	@Output() public onSaveButtonEnable: EventEmitter<boolean> = new EventEmitter();

	public siteNoteForm: FormGroup;
	public siteNoteRequest: SiteNoteRequest;

	public possibleAssets: SiteNoteResource[] = [];
	public possibleLocations: SiteNoteResource[] = [];

	get locationTypeId() { return this.siteNoteForm.get('locationTypeId'); }
	get assetTypeId() { return this.siteNoteForm.get('assetTypeId'); }

	private locationTypeChanged: boolean = false;
	private assetTypeChanged: boolean = false;

	constructor(
		private activatedRoute: ActivatedRoute,
		private siteNotesService: SiteNotesService
	) { }

	ngOnInit(): void {

		if (this.siteNote) {
			this.initSiteNoteEditForm();
			this.loadSiteNote(this.siteNote);
		} else {
			this.initSiteNoteAddForm();
		}

		this.siteNoteForm.get('locationTypeId').valueChanges.subscribe(value => {
			if(this.locationTypeChanged || !this.siteNote){
				this.siteNoteForm.patchValue({ "locationId": null });
			}
			this.locationTypeChanged = true;

			this.getAssets(value, true, false );

			// if(value){
			// 	this.addRequiredValidator('locationId');
			// } else {
			// 	this.removeValidators('locationId');
			// }
		});

		this.siteNoteForm.get('assetTypeId').valueChanges.subscribe(value => {
			if(this.assetTypeChanged || !this.siteNote){
				this.siteNoteForm.patchValue({ "assetId": null });
			}
			this.assetTypeChanged = true;
			this.getAssets(value, false, true );

			if(value){
				this.addRequiredValidator('assetId');
			} else {
				this.removeValidators('assetId');
			}
		});

		if(this.siteNote?.assetTypeId){
			this.siteNoteForm.patchValue({ "assetTypeId": this.siteNote?.assetTypeId });
		} else if(!this.siteNote && this.assetTypes.length == 1 ){
			this.siteNoteForm.patchValue({ "assetTypeId": this.assetTypes[0].resourceTypeId });
		}

		if(this.siteNote?.locationTypeId){
			this.siteNoteForm.patchValue({ "locationTypeId": this.siteNote?.locationTypeId });
		} else if(!this.siteNote && this.locationTypes.length == 1 ){
			this.siteNoteForm.patchValue({ "locationTypeId": this.locationTypes[0].resourceTypeId });
		}

		this.siteNoteForm.valueChanges.subscribe(value => {
			this.enableSaveButton(this.siteNoteForm.valid);
		})
	}

	private enableSaveButton(isEnable): void {
		this.onSaveButtonEnable.emit(isEnable);
	}

	private initSiteNoteAddForm(): void {
		this.siteNoteForm = new FormGroup({
			jobId: new FormControl(this.jobId),
			accountSiteId: new FormControl(this.accountSiteId),
			categoryId: new FormControl(1),
			message: new FormControl('', [Validators.required, Validators.minLength(1)]),
			isEmergency: new FormControl(false),
			notifyOthers: new FormControl(false),
			isCompletionRequired: new FormControl(false),
			assignedUserId: new FormControl(null),
			locationTypeId: new FormControl(null),
			locationId: new FormControl(null),
			assetTypeId: new FormControl(null),
			assetId: new FormControl(null)
		});
	}

	private initSiteNoteEditForm(): void {
		this.siteNoteForm = new FormGroup({
			siteNoteId: new FormControl(),
			message: new FormControl('', [Validators.required, Validators.minLength(1)]),
			categoryId: new FormControl(1),
			isEmergency: new FormControl(false),
			notifyOthers: new FormControl({ value: false, disabled: true }),
			isCompletionRequired: new FormControl(false),
			assignedUserId: new FormControl(null),
			locationTypeId: new FormControl(null),
			locationId: new FormControl(null),
			assetTypeId: new FormControl(null),
			assetId: new FormControl(null)
		});
	}

	private loadSiteNote(siteNote: SiteNote): void {
		var locationTypeId = (this.locationTypes.length == 0 || !siteNote?.locationTypeId)
							? null
							: this.locationTypes.findIndex(lt => (lt.resourceTypeId == siteNote.locationTypeId)) < 0 ? null : siteNote.locationTypeId;

		var	assetTypeId = (this.assetTypes.length == 0 || !siteNote?.assetTypeId)
							? null
							:this.assetTypes.findIndex(at => (at.resourceTypeId == siteNote.assetTypeId)) < 0 ? null : siteNote.assetTypeId;
		if (this.isOnCompleteNote) {
			this.siteNoteForm.patchValue({
				siteNoteId: siteNote?.siteNoteId,
				categoryId: siteNote.siteNoteCategoryId ?? 1,
				message: siteNote?.message,
				notifyOthers: siteNote.notifyOthers,
				isEmergency: siteNote.isEmergency,
				isCompletionRequired: siteNote?.isCompletionRequired,
				assignedUserId: siteNote?.assignedUserId ? siteNote.assignedUserName : '-None-',
				locationTypeId: siteNote?.locationTypeId,
				locationId: siteNote?.locationId,
				assetTypeId: siteNote?.assetTypeId,
				assetId: siteNote?.assetId
			});
			this.siteNoteForm.disable();
		} else {
			this.siteNoteForm.patchValue({
				siteNoteId: siteNote?.siteNoteId,
				categoryId: siteNote.siteNoteCategoryId ?? 1,
				message: siteNote?.message,
				notifyOthers: siteNote.notifyOthers,
				isEmergency: siteNote.isEmergency,
				isCompletionRequired: siteNote?.isCompletionRequired,
				assignedUserId: siteNote?.assignedUserId ? siteNote?.assignedUserId : 0,
				locationTypeId: locationTypeId,
				locationId: !locationTypeId ? null : siteNote?.locationId,
				assetTypeId: assetTypeId,
				assetId: !assetTypeId ? null : siteNote?.assetId
			});
		}
	}

	public getSiteNote(): void {
		const siteNote = this.siteNoteForm.getRawValue();
		if (this.siteNote) {
			this.onSiteNoteEditSave.emit(siteNote);
		} else {
			this.onSiteNoteAddSave.emit(siteNote);
		}

	}

	private getAssets(resourceTypeId: number, isLocation: boolean, isAsset: boolean): void {
		if(!resourceTypeId){
			return;
		}
		this.siteNotesService.getSiteNoteAssets(TokenHelper.getToken(this.activatedRoute), this.accountSiteId, resourceTypeId, isLocation, isAsset).subscribe(
			res => {
				if(isLocation){
					this.possibleLocations = res;
				} else if(isAsset){
					this.possibleAssets = res;
				}
			}, error => {
			ToastService.pushToast({
				text: 'Could not get types. Please try again!',
				icon: 'error',
				type: ToastType.Error
			});
		}
		)
	}

	private addRequiredValidator(control : string){
			this.siteNoteForm.get(control)?.setValidators(Validators.required);

			this.siteNoteForm.get(control)?.updateValueAndValidity();
	}
	
	private removeValidators(control : string){
			this.siteNoteForm.get(control)?.clearValidators();
			this.siteNoteForm.get(control)?.updateValueAndValidity();
	}
}
